import React, { useEffect, useRef, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx } from '@atlaskit/css';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import type { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const playerWidth = 380;
const miniplayerWidth = 274;
const initialTopPosition = 104;
const initialRightOffset = playerWidth + 32; // width + space.400

const styles = cssMap({
	floatingStyles: {
		position: 'fixed',
		// @ts-ignore: There are no space tokens larger than 80px (space.1000)
		top: `${initialTopPosition}px`, // under the top nav
		right: token('space.400'), // Tied to initialRightOffset
		zIndex: 510, // modal
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.xlarge'),
		textAlign: 'center',
	},
	idleStyles: {
		width: 'auto',
		minWidth: `${miniplayerWidth}px`, // width of minified player
		cursor: 'grab',
	},
	draggingStyles: {
		width: `${playerWidth}px`, // width of full player
		cursor: 'grabbing',
	},
});

type Position = {
	top: number;
	left: number;
};

// Prevents the miniplayer from being dragged past the boundaries of a given dimension of the window
const getBoundedDimension = (dimension: number, maxDimension: number): number => {
	if (dimension < 0 || dimension > maxDimension) {
		return dimension < 0 ? 0 : maxDimension;
	}

	return dimension;
};

// Prevent clicking from blurring/closing other UI elements like modals/dialogs
const stopClickPropagation: React.MouseEventHandler<HTMLElement> = (
	event: React.MouseEvent,
): void => event.stopPropagation();

export type DraggableAudioProps = {
	children: React.ReactNode;
};

export const DraggableAudio = ({ children }: DraggableAudioProps) => {
	const document = getDocument() || window.document;
	const draggableRef = useRef<HTMLElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [initialPosition, setInitialPosition] = useState<Position>({
		top: initialTopPosition,
		left: document.body.clientWidth - initialRightOffset,
	});
	const [currentPosition, setCurrentPosition] = useState<Position>(initialPosition);
	const defaultCursorPosition = { top: 0, left: 0 };
	const [, setCurrentCursorPosition] = useState<Position>(defaultCursorPosition);

	const setPosition = (element: HTMLElement, location: DragLocationHistory, isDrop = false) => {
		let previousCursorPositionX: number = 0;
		let previousCursorPositionY: number = 0;
		setCurrentCursorPosition((current) => {
			previousCursorPositionY = current.top;
			previousCursorPositionX = current.left;

			return isDrop
				? defaultCursorPosition
				: {
						top: location.current.input.clientY,
						left: location.current.input.clientX,
					};
		});

		const clientYDelta = previousCursorPositionY - location.current.input.clientY;
		const clientXDelta = previousCursorPositionX - location.current.input.clientX;
		setCurrentPosition((current) => {
			let newTop = current.top - clientYDelta;
			let newLeft = current.left - clientXDelta;

			// Prevent dragging outside the window boundary
			newTop = getBoundedDimension(newTop, window.innerHeight - element.offsetHeight);
			newLeft = getBoundedDimension(newLeft, document.body.clientWidth - element.offsetWidth);

			element.style.top = `${newTop}px`;
			element.style.left = `${newLeft}px`;

			return {
				top: newTop,
				left: newLeft,
			};
		});
	};

	useEffect(() => {
		const element = draggableRef.current;
		if (!element) {
			return;
		}

		return combine(
			draggable({
				element,
				onGenerateDragPreview({ nativeSetDragImage }) {
					// We'll drag the actual element so hide the native drag preview
					disableNativeDragPreview({ nativeSetDragImage });
				},
				onDragStart: ({ location }) => {
					setCurrentCursorPosition({
						top: location.initial.input.clientY,
						left: location.initial.input.clientX,
					});
					setInitialPosition(currentPosition);
					setIsDragging(true);
				},
				onDrag: ({ location }) => {
					setPosition(element, location);
				},
				onDrop: ({ location }) => {
					setPosition(element, location, true);
					setIsDragging(false);
				},
			}),
			dropTargetForElements({
				element: document.body,
			}),
		);
	});

	return (
		<Box
			ref={draggableRef}
			xcss={cx(styles.floatingStyles, isDragging ? styles.draggingStyles : styles.idleStyles)}
			onClick={stopClickPropagation}
			testId="audio-draggable"
			role="none"
		>
			{children}
		</Box>
	);
};
